import React from "react";
import { MessageSquare, ImageDown, Receipt, CheckCircle2 } from "lucide-react";

const steps = [
    {
        n: "01",
        icon: MessageSquare,
        title: "Request a quote",
        body: "Drop us a line through the form, by text, or by phone. Tell us what needs doing.",
    },
    {
        n: "02",
        icon: ImageDown,
        title: "Send job details or photos",
        body: "A few photos and your suburb is usually all we need. We’ll ask if anything’s unclear.",
    },
    {
        n: "03",
        icon: Receipt,
        title: "Get a clear price",
        body: "We reply within a day with a fixed price (or an honest hourly estimate) and a window that works.",
    },
    {
        n: "04",
        icon: CheckCircle2,
        title: "We complete the job",
        body: "We turn up, finish neat, clear the mess and send a quick after photo so you don’t have to chase.",
    },
];

export default function HowItWorks() {
    return (
        <section
            id="how"
            data-testid="section-how"
            className="relative bg-[var(--paper)] py-28 md:py-40 border-t border-line"
        >
            <div className="maxw section-pad">
                <div className="grid md:grid-cols-12 gap-10 items-end mb-16">
                    <div className="md:col-span-7 reveal">
                        <div className="eyebrow mb-5">How it works</div>
                        <h2 className="font-display font-light text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
                            Four steps from{" "}
                            <span className="italic text-[var(--terra)]">
                                first message
                            </span>{" "}
                            to tidy yard.
                        </h2>
                    </div>
                    <div className="md:col-span-5 md:text-right">
                        <div className="font-mono-x text-[12.5px] text-[rgba(14,14,16,0.6)]">
                            Usually under a week, start to finish.
                        </div>
                    </div>
                </div>

                <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {steps.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <li
                                key={s.n}
                                data-testid={`how-step-${s.n}`}
                                className="reveal relative border border-line bg-[rgba(233,226,211,0.4)] hover:bg-[var(--paper-2)] transition-colors rounded-2xl p-7 md:p-8 flex flex-col gap-6 min-h-[280px]"
                                style={{ transitionDelay: `${i * 90}ms` }}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="font-display text-[42px] leading-none tracking-tight text-[var(--ink)]">
                                        {s.n}
                                    </span>
                                    <span className="w-10 h-10 rounded-xl bg-[rgba(63,90,36,0.12)] text-[var(--terra)] flex items-center justify-center">
                                        <Icon size={18} strokeWidth={1.7} />
                                    </span>
                                </div>
                                <h3 className="font-display text-2xl md:text-[26px] font-medium tracking-tight">
                                    {s.title}
                                </h3>
                                <p className="text-[14.5px] leading-relaxed text-[rgba(14,14,16,0.68)]">
                                    {s.body}
                                </p>
                                <div className="hr-line mt-auto" />
                                <div className="flex items-center justify-between font-mono-x text-[10.5px] text-[rgba(14,14,16,0.48)]">
                                    <span>Step {Number(s.n)} / 4</span>
                                    <span>{s.title.toLowerCase().split(" ")[0]}</span>
                                </div>
                            </li>
                        );
                    })}
                </ol>

                <div className="mt-12 text-center md:text-left">
                    <a href="#contact" className="btn-ink" data-testid="how-cta">
                        Start with step one →
                    </a>
                </div>
            </div>
        </section>
    );
}
